<!--
Copyright (C) 2025 Checkmk GmbH - License: GNU General Public License v2
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->
<script setup lang="ts">
import type { Ref } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'

import { fetchRestAPI } from '@/lib/cmkFetch.ts'
import usei18n, { untranslated } from '@/lib/i18n'
import type { TranslatedString } from '@/lib/i18nString'

import CmkDropdown from '@/components/CmkDropdown'
import { type Suggestion } from '@/components/CmkSuggestions'

import DateRangeFields from './DateRangeFields.vue'
import DurationFields from './DurationFields.vue'
import type { PreDefinedTimeRange } from './types'

const { _t } = usei18n()

export interface GraphTimerange {
  type: 'predefined' | 'duration' | 'date' | 'age'
  duration: number | null
  date_range: null | {
    from: string
    to: string
  }
  predefined: PreDefinedTimeRange | null
  age: null | Age
}

export interface Age {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const predefinedTimeranges: Partial<Record<PreDefinedTimeRange, TranslatedString>> = {
  last_4_hours: _t('The last 4 hours'),
  today: _t('Today'),
  yesterday: _t('Yesterday'),
  '7_days_ago': _t('7 days back (this day last week)'),
  this_week: _t('This week'),
  last_week: _t('Last week'),
  '2_weeks_ago': _t('2 weeks back'),
  this_month: _t('This month'),
  last_month: _t('Last month'),
  this_year: _t('This year'),
  last_year: _t('Last year')
}

interface GraphTimerangeApiResult {
  title: string
  extensions: {
    total_seconds: number
  }
}

const selectedTimerange = defineModel<GraphTimerange>('selectedTimerange', { required: true })

const apiDurationTimeranges = ref<GraphTimerangeApiResult[]>([])
const selectedDropdownOption = ref<string | null>(null)

function toPublicTimerange(internal: GraphTimerangeApiResult): GraphTimerange {
  return {
    type: 'duration',
    duration: internal.extensions.total_seconds,
    date_range: null,
    predefined: null,
    age: null
  }
}

const customTimeOptionName = 'custom_time'
const customDateOptionName = 'custom_date'

const customTimeOptionTitle = _t('The last...')
const customDateOptionTitle = _t('Date range')

async function loadApiDurationGraphTimeranges(): Promise<GraphTimerangeApiResult[]> {
  const API_ROOT = 'api/unstable'
  const url = `${API_ROOT}/domain-types/graph_timerange/collections/all`
  const response = await fetchRestAPI(url, 'GET')
  await response.raiseForStatus()
  const data = await response.json()
  return data.value
}

const dropdownOptions = computed<Suggestion[]>(() => {
  const predefinedTitles = new Set(Object.values(predefinedTimeranges).map((t) => t.toString()))

  // API duration entries that have no predefined counterpart
  const extraApiRanges = apiDurationTimeranges.value
    .filter((range) => !predefinedTitles.has(range.title))
    .map((range) => ({
      name: `duration_${range.extensions.total_seconds?.toString() ?? '0'}`,
      title: untranslated(range.title)
    }))

  const predefinedRanges = Object.entries(predefinedTimeranges).map(([apiKey, title]) => ({
    name: apiKey,
    title: title
  }))

  const customOptions = [
    { name: customTimeOptionName, title: customTimeOptionTitle },
    { name: customDateOptionName, title: customDateOptionTitle }
  ]

  return [...extraApiRanges, ...predefinedRanges, ...customOptions]
})

const customDuration: Ref<Age> = ref({
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0
})

const customDurationDate = ref({
  from: {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate()
  },
  to: {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate()
  }
})

function getDropdownOptionFromTimerange(timerange: GraphTimerange): string | null {
  switch (timerange.type) {
    case 'duration': {
      // If this duration matches a predefined entry (via API title), use the predefined name.
      const matchingApi = apiDurationTimeranges.value.find(
        (r) => r.extensions.total_seconds === timerange.duration
      )
      if (matchingApi) {
        const predefinedEntry = Object.entries(predefinedTimeranges).find(
          ([, title]) => title.toString() === matchingApi.title
        )
        if (predefinedEntry) {
          return predefinedEntry[0]
        }
      }
      return `duration_${timerange.duration}`
    }
    case 'predefined':
      return timerange.predefined
    case 'age':
      return customTimeOptionName
    case 'date':
      return customDateOptionName
  }
}

onMounted(async () => {
  apiDurationTimeranges.value = await loadApiDurationGraphTimeranges()

  if (selectedTimerange.value) {
    selectedDropdownOption.value = getDropdownOptionFromTimerange(selectedTimerange.value)
    if (selectedTimerange.value.type === 'age' && selectedTimerange.value.age) {
      customDuration.value = { ...selectedTimerange.value.age }
    } else if (selectedTimerange.value.type === 'date' && selectedTimerange.value.date_range) {
      const fromDate = new Date(selectedTimerange.value.date_range.from)
      const toDate = new Date(selectedTimerange.value.date_range.to)
      customDurationDate.value = {
        from: {
          year: fromDate.getFullYear(),
          month: fromDate.getMonth() + 1,
          day: fromDate.getDate()
        },
        to: {
          year: toDate.getFullYear(),
          month: toDate.getMonth() + 1,
          day: toDate.getDate()
        }
      }
    }
  }
})

watch(
  [selectedDropdownOption, customDuration, customDurationDate],
  () => {
    const selectedOption: string = selectedDropdownOption.value ?? ''
    if (selectedOption.startsWith('duration_')) {
      const range = apiDurationTimeranges.value.find(
        (r) => `duration_${r.extensions.total_seconds?.toString() ?? '0'}` === selectedOption
      )
      if (range) {
        selectedTimerange.value = toPublicTimerange(range)
      }
    } else if (selectedOption === customTimeOptionName) {
      selectedTimerange.value = {
        type: 'age',
        age: { ...customDuration.value },
        duration: null,
        date_range: null,
        predefined: null
      }
    } else if (selectedOption === customDateOptionName) {
      const getApiDate = (date: { year: number; month: number; day: number }): string => {
        const paddedMonth = String(date.month).padStart(2, '0')
        const paddedDay = String(date.day).padStart(2, '0')
        return `${date.year}-${paddedMonth}-${paddedDay}`
      }

      selectedTimerange.value = {
        type: 'date',
        date_range: {
          from: getApiDate(customDurationDate.value.from),
          to: getApiDate(customDurationDate.value.to)
        },
        duration: null,
        predefined: null,
        age: null
      }
    } else if (selectedOption in predefinedTimeranges) {
      // If an API duration entry matches this predefined title, use the API version (API wins).
      const predefinedTitle = predefinedTimeranges[selectedOption as PreDefinedTimeRange]
      const matchingApi = predefinedTitle
        ? apiDurationTimeranges.value.find((r) => r.title === predefinedTitle.toString())
        : undefined
      if (matchingApi) {
        selectedTimerange.value = toPublicTimerange(matchingApi)
      } else {
        selectedTimerange.value = {
          type: 'predefined',
          predefined: selectedOption as PreDefinedTimeRange,
          duration: null,
          date_range: null,
          age: null
        }
      }
    }
  },
  { deep: true }
)

const isCustomTimeSelected = computed(() => selectedDropdownOption.value === customTimeOptionName)
const isCustomDateSelected = computed(() => selectedDropdownOption.value === customDateOptionName)
</script>

<template>
  <div>
    <CmkDropdown
      v-model:selected-option="selectedDropdownOption"
      :options="{ type: 'fixed', suggestions: dropdownOptions }"
      :label="_t('Time range')"
    />
    <DurationFields v-if="isCustomTimeSelected" v-model="customDuration" />
    <DateRangeFields v-if="isCustomDateSelected" v-model="customDurationDate" />
  </div>
</template>
